import {
	AccountLevelEnum,
	AccountStatusEnum,
	CarriersEnum,
	EnableDisableEnum,
	SCACEnum,
	SnackBarVariants,
	UserRole,
} from '@app/app.enum';
import { history } from '@app/app.routes';
import store from '@app/app.store';
import FedexNativeComponent from '@app/components/carriers/fedex-native.component';
import SCACListComponent from '@app/components/carriers/scac-list.component';
import { withAxiosCancellation } from '@app/components/core/axios-cancel.component';
import withSnackBar from '@app/components/core/message.component';
import CustomDialogBox from '@app/components/shared/dialog.component';
import Icon from '@app/components/shared/icon.component';
import * as constants from '@app/constants';
import { minimumAppsRequiredPerAccount as minimumAppsRequiredPerAccount } from '@app/constants';
import * as models from '@app/models';
import Injector from '@app/services';
import { AccountService } from '@app/services/account.service';
import { CarrierAccountService } from '@app/services/carrier-account.service';
import { IntlService } from '@app/services/intl.service';
import { SharedService } from '@app/services/shared.service';
import {
	Button,
	Card,
	CardActions,
	CardContent,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	Menu,
	MenuItem,
	Step,
	StepLabel,
	Stepper,
	TextField,
	Toolbar,
	Tooltip,
	Typography,
} from '@material-ui/core';
import Axios, { CancelTokenSource } from 'axios';
import { Field, Form, Formik, FormikProps, FormikValues } from 'formik';
import PopupState, { bindMenu, bindTrigger } from 'material-ui-popup-state';
import * as React from 'react';
import { FormattedDate, injectIntl } from 'react-intl';
import * as Yup from 'yup';
import CarrierDetailsComponent from '../../carriers/carrier-details.component';
import CarriersListComponent from '../../carriers/carriers-list.component';
import UPSNativeComponent from '../../carriers/ups-native.component';
import USPSConfigComponent from '../../carriers/usps-config.component';
import TierOverrideComponent from '@app/components/carriers/tier-override.component';
import { AdminCarrierAccountService } from '@app/services/admin-carrier-account.service';
import _ from 'lodash';

const intlService: IntlService = Injector.get(IntlService);
const accountService: AccountService = Injector.get(AccountService);
const sharedService: SharedService = Injector.get(SharedService);
const carrierAccountService: CarrierAccountService = Injector.get(CarrierAccountService);
const adminCarrierAccountService: AdminCarrierAccountService = Injector.get(AdminCarrierAccountService);

let carrierData: models.ICarrierAccounts = {
	id: null,
	name: null,
	logo: null,
	carrierCode: null,
	description: null,
	carrierDescription: null,
	configuration: null,
	status: null,
	scac: null,
};

const baseSteps: string[] = [
	intlService.GetFormattedMessage('app.app-details-label'),
	intlService.GetFormattedMessage('app.scac-select-label'),
	intlService.GetFormattedMessage('app.carrier-select-label'),
];

const initialSelectedCarrier: models.ICarrier = {
	id: null,
	code: null,
	scac: null,
	name: null,
	logo: null,
	description: null,
	paymentType: null,
	provider: null,
	status: null,
};

const AddNewAppSchema = Yup.object().shape({
	appName: Yup.string()
		.max(100, intlService.GetFormattedMessage('app.app-view.app-card.new-app-add-appName-max-validation'))
		.required(intlService.GetFormattedMessage('app.app-view.app-card.new-app-add-appName-required-validation')),
	appDescription: Yup.string()
		.max(500, intlService.GetFormattedMessage('app.app-view.app-card.new-app-add-appDescription-max-validation'))
		.required(intlService.GetFormattedMessage('app.app-view.app-card.new-app-add-appDescription-required-validation')),
	apiVersion: Yup.string()
		.max(100, intlService.GetFormattedMessage('app.app-view.app-card.new-app-add-apiVersion-max-validation'))
		.required(intlService.GetFormattedMessage('app.app-view.app-card.new-app-add-apiVersion-required-validation')),
	helpUrl: Yup.string()
		.url(intlService.GetFormattedMessage('app.app-view.app-card.new-app-add-helpUrl-max-validation'))
		.required(intlService.GetFormattedMessage('app.app-view.app-card.new-app-add-helpUrl-required-validation')),
});

const CustomDialogButtonTextConfigs = {
	YesButtonText: intlService.GetFormattedMessage('app.common.dialog-okay-button-text'),
	passCustomText: false,
};

class AccountAppCardComponent extends React.Component<models.IAccountAppCardProps, models.IAccountAppCardState> {
	public carrierConfigRef: React.MutableRefObject<any>;
	public carrierDetailRef: React.MutableRefObject<any>;
	public tierOverrideConfigRef: React.MutableRefObject<any>;
	private userData = accountService.getUserData();
	private isPartnerOwnApp = this.userData.account.id === (this.props.app && this.props.app.partnerAccountId);
	private appFormRef: React.MutableRefObject<Formik> = React.createRef();
	private isPartnerAccount: boolean;
	private isAppEnabled: boolean;

	constructor(props: models.IAccountAppCardProps) {
		super(props);
		const isAccountClosed = store.getState().authentication.status === AccountStatusEnum.Closed;

		this.isAppEnabled = this.props?.app?.status === EnableDisableEnum.Enabled;
		this.state = models.initialAppCardState(this.props.app, baseSteps, isAccountClosed, initialSelectedCarrier);
		this.carrierConfigRef = React.createRef();
		this.carrierDetailRef = React.createRef();
		this.tierOverrideConfigRef = React.createRef();
		this.updateSelectedCarrier = this.updateSelectedCarrier.bind(this);
		this.isPartnerAccount = store.getState().authentication.user.account.level === AccountLevelEnum.Partner;
	}

	public componentDidUpdate(prevProps: models.IAccountAppCardProps, prevState: models.IAccountAppCardState) {
		if (prevState.selectedCarrier !== this.state.selectedCarrier) {
			const { code, name, description } = this.state.selectedCarrier;
			this.updateSteps(code, baseSteps);
			this.setState({
				carrierDetails: {
					name: name || '',
					description: '',
					carrierDescription: description || '',
				},
			});
		}

		if (
			prevState.activeStep !== this.state.activeStep &&
			(this.state.activeStep === 1 || this.state.activeStep === 0)
		) {
			this.setState({
				carrierDetails: {
					name: '',
					description: '',
					carrierDescription: '',
				},
				selectedSCAC: null,
			});
		}

		this.isAppEnabled = this.props?.app?.status === EnableDisableEnum.Enabled;
	}

	public render() {
		let renderMenu = null;
		const manageAppForm = this.renderManageAppDialog(this.state.manageAppModalOpen);
		if (
			(this.userData.user.role === UserRole.AccountAdmin ||
				accountService.IsAdmin(this.userData.user.role) ||
				this.isPartnerOwnApp) &&
			!this.state.isAccountClosed
		) {
			renderMenu = this.renderMenu();
		}

		if (this.state.isNewApp) {
			return this.renderNewApp(manageAppForm, this.props.showNewAppCardAsButton);
		}

		return (
			<React.Fragment>
				{/* Custom dialog box */}

				{/* #start region */}
				{/* Delete app dialog */}
				{this.state.openDeleteDialog && (
					<CustomDialogBox
						fullWidth={true}
						open={this.state.openDeleteDialog}
						dialogTitle={this.state.dialogTitle}
						loading={this.state.dialogButtonLoading}
						disabled={this.state.dialogButtonLoading}
						closeCustomDialog={this.closeDeleteDialog}
						dialogDescription={this.state.dialogDescription}
						dialogYesButton={
							CustomDialogButtonTextConfigs.passCustomText ? CustomDialogButtonTextConfigs.YesButtonText : undefined
						}
					/>
				)}

				{/* Disable/Enable app dialog */}
				{this.state.openAppStatusDialog && (
					<CustomDialogBox
						fullWidth={true}
						open={this.state.openAppStatusDialog}
						closeCustomDialog={this.closeAppStatusDialog}
						dialogTitle={this.state.dialogTitle}
						dialogDescription={this.state.dialogDescription}
						loading={this.state.dialogButtonLoading}
						disabled={this.state.dialogButtonLoading}
					/>
				)}
				{/* #end region */}

				<div
					className={`${this.props.classes.cardWidth} ${this.isAppEnabled ? this.props.classes.card : 'disabled-app'}`}
				>
					<Toolbar variant='dense' className='card-toolbar-header pr-0'>
						<Tooltip title={this.state.app.appName} placement='top-end'>
							<Typography className='card-heading' component='h5' color='inherit' noWrap={true}>
								{this.state.app.appName}
							</Typography>
						</Tooltip>
						<div className='flex-spacer' />
						{renderMenu}
					</Toolbar>
					<Card
						className='card-body'
						square={true}
						onClick={(e) => {
							if (this.isAppEnabled) {
								this.props.handleClick(e, this.state.app);
							}
						}}
					>
						<CardContent>{this.renderAppContent(this.state.app)}</CardContent>
						<CardActions>
							<Typography variant='caption' noWrap={true}>
								<i>
									{intlService.GetFormattedMessage('app.app-view.app-card.app-edit-appType-published-by')}
									{this.state.app.partnerName}
								</i>
							</Typography>
						</CardActions>
					</Card>
					{manageAppForm}
				</div>
			</React.Fragment>
		);
	}

	private renderAppContent(app: models.IAccountAppResponseVM) {
		const apiVersion = app.apiVersion;
		return (
			<>
				<Typography variant='body2' gutterBottom={true}>
					{intlService.GetFormattedMessage('app.app-view.app-card.app-edit-appType-api-version')}: {apiVersion}
				</Typography>
				<Typography className={this.props.classes.textLabel} variant='body2'>
					{`${intlService.GetFormattedMessage('app.app-view.app-installed-on')}: `}
					<FormattedDate
						{...constants.dateFormatOptions}
						value={sharedService.getNewDateIfNotExist(this.state.app.dateCreated)}
					/>
				</Typography>
			</>
		);
	}

	private renderNewApp(manageAppForm: JSX.Element, showNewAppCardAsButton: boolean) {
		return (
			<>
				{showNewAppCardAsButton ? (
					<Grid container={true} justifyContent='space-between'>
						<Grid item={true}>
							<Button startIcon={<Icon name='plus' />} variant='text' onClick={() => this.manageAppDialog(true)}>
								<strong>{intlService.GetFormattedMessage('app.app-card.add-new-label')}</strong>
							</Button>
						</Grid>
						<Grid item={true}>{this.props.appFilterButton}</Grid>
					</Grid>
				) : (
					<Card
						className={`${this.props.classes.card} ${this.props.classes.cardWidth} ${this.props.classes.cardHeight}`}
						square={true}
						onClick={() => this.manageAppDialog(true)}
					>
						<CardContent className='height-100'>
							<Grid container={true} direction='row' justifyContent='center' alignItems='center' className='height-100'>
								<Grid item={true}>
									<Typography variant='h5' align='center'>
										<Icon name='plus' />
									</Typography>
									<Typography variant='h6' align='center'>
										{intlService.GetFormattedMessage('app.app-view.add-app')}
									</Typography>
								</Grid>
							</Grid>
						</CardContent>
					</Card>
				)}
				{manageAppForm}
			</>
		);
	}

	private renderMenu() {
		return (
			!this.isPartnerAccount && (
				<PopupState variant='popper'>
					{(popupState: models.IMenuInjectedProps) => (
						<React.Fragment>
							<IconButton {...bindTrigger(popupState)} color='inherit'>
								<Icon name='ellipsis-vertical' className='icon-dimension-square' />
							</IconButton>
							<Menu {...bindMenu(popupState)}>
								{this.isAppEnabled && (
									<MenuItem onClick={() => this.manageAppDialog(true, popupState)}>
										{intlService.GetFormattedMessage('app.app-view.app-card.edit')}
									</MenuItem>
								)}

								{/* Delete app option */}
								{this.isAppEnabled && (
									<MenuItem
										onClick={() => {
											const dialogDescriptionArg1 = intlService.GetFormattedMessage('app.user.delete');
											const dialogDescriptionArg2 = this.state.app.appName;

											// show error message if app cannot be deleted and user is trying to delete it
											if (this.props.totalNumberOfApps <= minimumAppsRequiredPerAccount) {
												this.onAttemptToDeleteOrDisableAppsBeyondMinimumNumber();
												return;
											}

											this.setState({
												openDeleteDialog: true,
												dialogTitle: intlService.GetFormattedMessage('app.delete-app-label'),
												dialogDescription: intlService.GetFormattedMessage(
													'app.generic.delete-action-dialog-description',
													{
														[0]: dialogDescriptionArg1.toLowerCase(),
														[1]: dialogDescriptionArg2,
													}
												),
											});
											popupState.close();
										}}
									>
										{intlService.GetFormattedMessage('app.user.delete')}
									</MenuItem>
								)}

								{/* Enable / Disable app option */}
								<MenuItem
									onClick={() => {
										const dialogDescriptionArg1 = intlService.GetFormattedMessage(
											this.isAppEnabled ? 'app.user.disable' : 'app.user.enable'
										);
										const dialogDescriptionArg2 = this.state.app.appName;

										// show error message if app cannot be disabled and user is trying to disable it
										if (this.isAppEnabled && this.props.totalNumberOfApps <= minimumAppsRequiredPerAccount) {
											this.onAttemptToDeleteOrDisableAppsBeyondMinimumNumber();
											return;
										}

										this.setState({
											openAppStatusDialog: true,
											dialogTitle: dialogDescriptionArg1,
											dialogDescription: intlService.GetFormattedMessage(
												'app.generic.delete-action-dialog-description',
												{
													[0]: dialogDescriptionArg1.toLowerCase(),
													[1]: dialogDescriptionArg2,
												}
											),
										});
										popupState.close();
									}}
								>
									{intlService.GetFormattedMessage(this.isAppEnabled ? 'app.user.disable' : 'app.user.enable')}
								</MenuItem>
							</Menu>
						</React.Fragment>
					)}
				</PopupState>
			)
		);
	}

	private onAttemptToDeleteOrDisableAppsBeyondMinimumNumber = () => {
		this.props.openSnackBar(
			intlService.GetFormattedMessage('app.snackbar.minimum-apps-required-per-account', {
				0: minimumAppsRequiredPerAccount,
			}),
			SnackBarVariants.Error
		);
	};

	private renderManageAppDialog(manageAppModalOpen: boolean) {
		const app = { ...this.state.app };
		const dialogCancellationSource = Axios.CancelToken.source();

		// Constant values for apiVersion and helpUrl
		app.apiVersion = Object.keys(this.props.apiVersion)[0];
		const helpUri = Object.values(this.props.apiVersion)[0];
		app.helpUrl = helpUri && helpUri[0];

		return (
			<>
				<Formik
					initialValues={app}
					validationSchema={AddNewAppSchema}
					enableReinitialize={true}
					ref={this.appFormRef}
					onSubmit={async (values, actions) => {
						try {
							this.setState((prevState: models.IAccountAppCardState) => ({
								activeStep: prevState.activeStep + 1,
							}));
							let response: models.IAccountAppResponseVM;
							if (this.state.isNewApp) {
								const accountId = accountService.IsAdmin(this.userData.user.role)
									? history.location.state.account.id
									: null;
								this.setState({
									newAppFormData: { ...(values as models.IAccountAppResponseVM), accountId },
								});
							} else {
								response = (
									await accountService.editApp(values as models.IAccountAppResponseVM, dialogCancellationSource.token)
								).data;

								app.appName = response.appName;
								app.appDescription = response.appDescription;
								app.apiVersion = response.apiVersion;
								app.helpUrl = response.helpUrl;

								const userData = accountService.getUserData();

								if (!accountService.IsAdmin(userData.user.role)) {
									const userApp = userData.apps.find((x: models.IAccountAppResponseVM) => x.id === app.id);

									Object.assign(userApp, response);

									accountService.setUserData(userData);
								}

								this.props.onAppUpdate(app);

								this.setState({
									app,
									manageAppModalOpen: false,
								});
								actions.resetForm();
							}
						} catch (e) {
							if (!Axios.isCancel(e)) {
								this.props.openSnackBar(sharedService.parseError(e.data), SnackBarVariants.Error);
							}
						}
					}}
					render={(props: FormikProps<FormikValues>) => (
						<Form>
							<Dialog
								open={manageAppModalOpen}
								onClose={() => this.manageAppDialog(false, null, dialogCancellationSource)}
								onExited={() => {
									props.resetForm();
								}}
								fullWidth={true}
								maxWidth='md'
								classes={{ paper: 'dialog-min-height' }}
							>
								<DialogTitle
									style={{
										paddingBottom: '0',
									}}
								>
									<Grid container={true} spacing={2}>
										<Grid item={true} xs={12}>
											{this.props.intl.formatMessage({
												id: this.state.isNewApp ? 'app.app-view.add-app' : 'app.app-view.edit-app',
											})}
										</Grid>
										{this.state.isNewApp && (
											<Grid item={true} xs={12}>
												<Stepper
													classes={{
														root: 'app-dialog-stepper',
													}}
													activeStep={this.state.activeStep}
												>
													{this.state.steps.map((label) => (
														<Step key={label}>
															<StepLabel>{label}</StepLabel>
														</Step>
													))}
												</Stepper>
											</Grid>
										)}
									</Grid>
								</DialogTitle>
								<DialogContent classes={{ root: 'dialog-content' }}>
									{this.dialogContent(this.state.isNewApp, manageAppModalOpen, props)}
								</DialogContent>
								<DialogActions>
									{this.state.manageAppModalOpen &&
										this.dialogButtonsList(
											dialogCancellationSource,
											props.submitForm,
											props.isSubmitting,
											props.errors
										)}
								</DialogActions>
							</Dialog>
						</Form>
					)}
				/>
			</>
		);
	}

	private manageAppDialog(
		open: boolean,
		popupState?: models.IMenuInjectedProps,
		dialogCancellationSource?: CancelTokenSource
	) {
		if (popupState) {
			popupState.close();
		}

		if (!open && !!dialogCancellationSource) {
			dialogCancellationSource.cancel();
		}

		if (!open && !!this.props.onNewAppAdd) {
			this.setState({
				app: models.initialApp(),
			});
		}

		this.setState({
			manageAppModalOpen: open,
			selectedCarrier: initialSelectedCarrier,
			activeStep: 0,
		});
	}

	private async addNewApp(
		carrierDetails: models.ICarrierAccounts,
		carrierConfiguration: any,
		tierOverrideConfiguration: models.ITierOverrideConfigs
	) {
		try {
			const { code, description } = this.state.selectedCarrier;

			this.setState({ showLoader: true });

			let response = null;
			// Create new app
			if (!this.state.app.appId) {
				response = (await accountService.addApp(this.state.newAppFormData)).data;

				this.props.onNewAppAdd(response);
				this.setState({ app: response });
			}

			// If user is ADMIN then pass accountId of the user against whom admin is trying to add app
			const accountId = accountService.IsAdmin(this.userData.user.role) ? history.location.state.account.id : null;

			// Insert Carrier configurations
			const result = await this.addCarrierConfigurations(carrierConfiguration, code, accountId);
			carrierDetails.configuration = { ...result };
			carrierDetails.carrierCode = code;
			carrierDetails.carrierDescription = description;
			carrierDetails.accountId = accountId;

			// Create CarrierAccount with the user provided details
			const carrierAccount = await carrierAccountService.CreateCarrierAccount(
				carrierDetails,
				(response && response.appId) || this.state.app.appId
			);

			if (tierOverrideConfiguration !== null) {
				await adminCarrierAccountService.UpsertTierOverrideCarrierAccountConfig(
					carrierAccount.id,
					tierOverrideConfiguration
				);
			}

			this.props.openSnackBar(
				this.props.intl.formatMessage({
					id: 'app.snackbar.app-added-successfully',
				})
			);

			this.setState({
				app: models.initialApp(),
				activeStep: 0,
				showLoader: false,
				selectedCarrier: initialSelectedCarrier,
				manageAppModalOpen: false,
			});
		} catch (e) {
			this.setState({
				showLoader: false,
			});
			if (!Axios.isCancel(e)) {
				this.props.openSnackBar(sharedService.parseError(e.data), SnackBarVariants.Error);
			}
		}
	}

	private async addCarrierConfigurations(carrierConfiguration: any, carrierType: string, accountId: string) {
		try {
			let result: models.ICarrierAccountConfigurations = null;
			switch (carrierType) {
				case CarriersEnum.USPSeVS:
				case CarriersEnum.USPSeVSNative:
				case CarriersEnum.USPSePostage:
				case CarriersEnum.USPSePostageNative:
				case CarriersEnum.USPSShip:
				case CarriersEnum.USPSePostageWarehouse:
				case CarriersEnum.USPSePostagePostPay:
					const configAddress: models.ISenderInfoFields = {
						company: carrierConfiguration.company,
						street1: carrierConfiguration.street1,
						street2: carrierConfiguration.street2,
						street3: carrierConfiguration.street3,
						city: carrierConfiguration.city,
						state: carrierConfiguration.state,
						zip: carrierConfiguration.zip,
						country: carrierConfiguration.country,
						phone: carrierConfiguration.phone,
						email: carrierConfiguration.email,
						name: carrierConfiguration.name,
					};

					const savedAddress = await this.createCarrierConfigAddress(configAddress, carrierType, accountId);

					result = {
						...savedAddress,
						RatingMode: carrierConfiguration.ratingMode,
						PrintAltReturnAddressOnUSPSLabel: carrierConfiguration.printAltReturnAddressOnUSPSLabel,
						USPSPrintEntryFacilityNameOnLabel: carrierConfiguration.USPSPrintEntryFacilityNameOnLabel,
						MRAReturnsMid: carrierConfiguration.mraReturnsMid,
						MRAAccountCode: carrierConfiguration.mraAccountCode,
					};

					// Add ship-api configs if its admin
					if (carrierType === CarriersEnum.USPSShip && accountService.IsAdmin(this.userData.user.role)) {
						result = {
							...result,
							LabelOwner_MID: carrierConfiguration.LabelOwner_MID,
							LabelOwner_ManifestMID: carrierConfiguration.LabelOwner_ManifestMID,
							MailOwner_MID: carrierConfiguration.MailOwner_MID,
							Payer_CRID: carrierConfiguration.Payer_CRID,
							LabelOwner_CRID: carrierConfiguration.LabelOwner_CRID,
							ReturnLabelOwner_CRID: carrierConfiguration.ReturnLabelOwner_CRID,
							ReturnLabelOwner_MID: carrierConfiguration.ReturnLabelOwner_MID,
							ReturnLabelOwner_ManifestMID: carrierConfiguration.ReturnLabelOwner_ManifestMID,
							Shipper_CRID: carrierConfiguration.Shipper_CRID,
							ConsumerKey: carrierConfiguration.ConsumerKey,
							ConsumerSecret: carrierConfiguration.ConsumerSecret,
							Payer_EPSAccountNumber: carrierConfiguration.Payer_EPSAccountNumber,
							Payer_PermitNumber: carrierConfiguration.Payer_PermitNumber,
							Payer_PermitZIP: carrierConfiguration.Payer_PermitZIP,
							Payer_AccountType: carrierConfiguration.Payer_AccountType,
							Shipper_MID: carrierConfiguration.Shipper_MID,
						};
					}

					break;
				case CarriersEnum.UPSNative:
					result = { AccountNumber: carrierConfiguration.accountNumber };
					break;
				case CarriersEnum.FedExNative:
					result = {
						MeterNumber: carrierConfiguration.meterNumber,
						BillingAccountNumber: carrierConfiguration.billingAccountNumber,
						BillingGroundAccountNumber: carrierConfiguration.billingGroundAccountNumber,
						ShipperAccountNumber: carrierConfiguration.shipperAccountNumber,
						ShipperGroundAccountNumber: carrierConfiguration.shipperGroundAccountNumber,
						SmartPostId: carrierConfiguration.isSmartPostEnabled ? carrierConfiguration.smartPostId : null,
						SmartPostHubId: carrierConfiguration.isSmartPostEnabled ? carrierConfiguration.smartPostHubId : null,
						SmartPostMailerId: carrierConfiguration.isSmartPostEnabled ? carrierConfiguration.smartPostMailerId : null,
						SmartPostDistributionCenter: carrierConfiguration.isSmartPostEnabled
							? carrierConfiguration.smartPostDistributionCenter
							: null,
						GroundEconomyPickUpCarrier: carrierConfiguration.isSmartPostEnabled
							? carrierConfiguration.groundEconomyPickUpCarrier
							: null,
						RatingMode: carrierConfiguration.ratingMode,
					};
					break;
				case CarriersEnum.USPSeVSWarehouse:
				case CarriersEnum.USPSPHv2:
				case CarriersEnum.UPSPHv2:
				case CarriersEnum.DHLExpressPHv2:
				default:
					return null;
			}
			return result;
		} catch (err) {
			throw err;
		}
	}

	private async createCarrierConfigAddress(
		data: models.ISenderInfoFields,
		carrierType: CarriersEnum,
		accountId: string
	): Promise<models.ICarrierAccountConfigurations> {
		try {
			data.verify = true;
			data.save = true;
			const result = await accountService.createCarrierConfigAddress(data, carrierType, accountId);
			return {
				ShipFromAddressId: result.id,
			};
		} catch (error) {
			throw error;
		}
	}

	private dialogContent(isNew: boolean, mounted: boolean, formikProps: FormikProps<FormikValues>) {
		const appFormFields = (
			<>
				<Field
					autoFocus={true}
					label={this.props.intl.formatMessage({
						id: 'app.app-view.app-name',
					})}
					margin='normal'
					variant='outlined'
					fullWidth={true}
					name='appName'
					value={formikProps.values.appName}
					component={TextField}
					error={!!formikProps.errors.appName}
					helperText={formikProps.errors.appName}
					onChange={formikProps.handleChange('appName')}
				/>
				<Field
					label={this.props.intl.formatMessage({
						id: 'app.app-view.app-description',
					})}
					margin='normal'
					variant='outlined'
					fullWidth={true}
					name='appDescription'
					value={formikProps.values.appDescription}
					component={TextField}
					error={!!formikProps.errors.appDescription}
					helperText={formikProps.errors.appDescription}
					onChange={formikProps.handleChange('appDescription')}
					multiline={true}
					rowsMax={6}
					rows={6}
				/>

				<Field
					fullWidth={true}
					component={TextField}
					disabled={true}
					margin='normal'
					label={this.props.intl.formatMessage({
						id: 'app.app-view.api-version',
					})}
					name='apiVersion'
					variant='outlined'
					value={formikProps.values.apiVersion}
				/>
				<Field
					fullWidth={true}
					component={TextField}
					disabled={true}
					margin='normal'
					label={this.props.intl.formatMessage({
						id: 'app.app-view.help-url',
					})}
					name='helpUrl'
					variant='outlined'
					value={formikProps.values.helpUrl}
				/>
			</>
		);
		if (isNew) {
			return (
				<>
					{
						// IIFE is used here to handle switch case
						(() => {
							const isCarrierSelected = this.state.selectedCarrier.name && this.state.selectedCarrier.name.length > 0;
							const component = [
								<React.Fragment key={0}>{appFormFields}</React.Fragment>,
								<React.Fragment key={1}>
									<SCACListComponent
										onClick={(selectedSCAC: SCACEnum) => {
											this.setState({
												selectedSCAC,
											});
										}}
									/>
								</React.Fragment>,
								<React.Fragment key={2}>
									<CarriersListComponent
										new={true}
										onClick={this.updateSelectedCarrier}
										userType={this.userData.user.role}
										appliedSCAC={this.state.selectedSCAC}
										selectedCarrierCode={this.state.selectedCarrier.code}
									/>
									<CarrierDetailsComponent
										isCreatingNewApp={true}
										disableAppDropDown={true}
										ref={this.carrierDetailRef}
										initialValues={{
											...this.state.carrierDetails,
											appName: formikProps.values.appName,
											description:
												isCarrierSelected &&
												(_.isNil(this.state.carrierDetails.description) ||
													_.isEmpty(this.state.carrierDetails.description))
													? intlService.GetFormattedMessage('app.carrier-description-default-value', {
															0: this.state.selectedCarrier.name || '',
													  })
													: this.state.carrierDetails?.description,
										}}
									/>
								</React.Fragment>,
								<React.Fragment key={3}>{this.getCarrierConfigurationComponent()}</React.Fragment>,
								<React.Fragment key={3}>
									{this.shouldDisplayTierOverride(this.state.selectedCarrier.code) && (
										<TierOverrideComponent
											ref={this.tierOverrideConfigRef}
											selectedCarrierName={this.state.selectedCarrier.name}
											selectedCarrierLogo={this.state.selectedCarrier.logo}
											carrierCode={this.state.selectedCarrier.code}
										/>
									)}
								</React.Fragment>,
							];
							return component[this.state.activeStep];
						})()
					}
				</>
			);
		}

		return appFormFields;
	}

	private dialogButtonsList(
		cancellationSource: CancelTokenSource,
		onSubmit: any,
		editFormSubmitting: boolean,
		errors: any
	): JSX.Element {
		let buttons = (
			<>
				<Button
					color='primary'
					variant='outlined'
					onClick={() => this.manageAppDialog(false, null, cancellationSource)}
				>
					{this.props.intl.formatMessage({
						id: 'app.app-view.cancel-button',
					})}
				</Button>
				<Button onClick={onSubmit} color='secondary' disabled={editFormSubmitting}>
					{this.props.intl.formatMessage({
						id: 'app.app-view.save-button',
					})}
					{editFormSubmitting && <CircularProgress size={24} className='ml-1' />}
				</Button>
			</>
		);

		if (!this.state.isNewApp) {
			return buttons;
		}

		const stepperFormButtons = this.updateDialogButtonsListOnStepsUpdate(cancellationSource, errors);

		buttons = stepperFormButtons[this.state.activeStep];

		return buttons;
	}

	private updateDialogButtonsListOnStepsUpdate(
		cancellationSource: CancelTokenSource,
		appFormErrors: any
	): JSX.Element[] {
		const middleStepsButtons = (onBack: () => void, onSave: () => void, disabled: boolean) => (
			<>
				<Button color='primary' variant='outlined' onClick={() => onBack()}>
					{this.props.intl.formatMessage({
						id: 'app.app-view.back-button',
					})}
				</Button>
				<Button onClick={() => onSave()} color='secondary' disabled={disabled}>
					{this.props.intl.formatMessage({
						id: 'app.app-view.next-button',
					})}
				</Button>
			</>
		);

		const finalStepButtons = (disableSave: boolean, onBack: () => void, onSave: () => void) => (
			<>
				<Button color='primary' variant='outlined' onClick={() => onBack()}>
					{this.props.intl.formatMessage({
						id: 'app.app-view.back-button',
					})}
				</Button>
				<Button onClick={() => onSave()} color='secondary' disabled={disableSave || this.state.showLoader}>
					{this.props.intl.formatMessage({
						id: 'app.app-view.save-button',
					})}
					{this.state.showLoader && <CircularProgress size={24} className='ml-1' />}
				</Button>
			</>
		);

		const dialogButtons: JSX.Element[] = [
			<>
				<Button
					color='primary'
					variant='outlined'
					onClick={() => this.manageAppDialog(false, null, cancellationSource)}
				>
					{this.props.intl.formatMessage({
						id: 'app.app-view.cancel-button',
					})}
				</Button>
				<Button
					onClick={() => {
						this.appFormRef.current.submitForm();
					}}
					color='secondary'
					disabled={!!Object.keys(appFormErrors).length}
				>
					{this.props.intl.formatMessage({
						id: 'app.app-view.next-button',
					})}
				</Button>
			</>,
		];

		const onBackHandler = () =>
			this.setState((prevState: models.IAccountAppCardState) => ({
				activeStep: prevState.activeStep - 1,
			}));

		const onNextHandler = async () => {
			this.setState((prevState: models.IAccountAppCardState) => ({
				activeStep: prevState.activeStep + 1,
			}));
		};

		const scacSelectionButton = middleStepsButtons(onBackHandler, onNextHandler, !this.state.selectedSCAC);

		if (this.state.steps.length === baseSteps.length) {
			const onBack = () =>
				this.setState((prevState: models.IAccountAppCardState) => ({
					activeStep: prevState.activeStep - 1,
					selectedCarrier: initialSelectedCarrier,
				}));

			const onSave = async () => this.saveCarrierDetails();

			dialogButtons.push(scacSelectionButton, finalStepButtons(!this.state.selectedCarrier.code, onBack, onSave));
		} else if (this.state.steps.length === baseSteps.length + 1) {
			const onBackFinalStep = () => {
				this.setState((prevState: models.IAccountAppCardState) => ({
					activeStep: prevState.activeStep - 1,
					carrierConfig: null,
				}));
			};

			const onSaveFinalStep = async () => {
				try {
					const configDetails = await this.carrierConfigRef.current.getWrappedInstance().getFormValues();
					if (!!configDetails) {
						this.addNewApp(carrierData, configDetails, null);
					}
				} catch (err) {
					throw err;
				}
			};

			const onBackMiddleStep = () =>
				this.setState((prevState: models.IAccountAppCardState) => ({
					activeStep: prevState.activeStep - 1,
					selectedCarrier: initialSelectedCarrier,
					carrierDetails: null,
				}));

			const onSaveMiddleStep = async () => {
				const res = await this.updateCarrierDetails();
				if (res) {
					this.setState((prevState: models.IAccountAppCardState) => ({
						activeStep: prevState.activeStep + 1,
					}));
				}
			};

			dialogButtons.push(
				scacSelectionButton,
				middleStepsButtons(onBackMiddleStep, onSaveMiddleStep, !this.state.selectedCarrier),
				finalStepButtons(false, onBackFinalStep, onSaveFinalStep)
			);
		} else {
			const onBackFinalStep = () => {
				this.setState((prevState: models.IAccountAppCardState) => ({
					activeStep: prevState.activeStep - 1,
				}));
			};
			const onSaveFinalStep = async () => {
				try {
					const tierConfigDetails = await this.tierOverrideConfigRef.current.getWrappedInstance().getFormValues();
					if (!!tierConfigDetails) {
						this.addNewApp(carrierData, this.state.carrierConfig, tierConfigDetails);
					}
				} catch (err) {
					throw err;
				}
			};

			const onBack2ndMiddleStep = () => {
				this.setState((prevState: models.IAccountAppCardState) => ({
					activeStep: prevState.activeStep - 1,
					carrierConfig: null,
				}));
			};
			const onNext2ndMiddleStep = async () => {
				const updated = await this.updateCarrierConfig();
				if (updated) {
					this.setState((prevState: models.IAccountAppCardState) => ({
						activeStep: prevState.activeStep + 1,
					}));
				}
			};

			const onBackMiddleStep = () =>
				this.setState((prevState: models.IAccountAppCardState) => ({
					activeStep: prevState.activeStep - 1,
					selectedCarrier: initialSelectedCarrier,
					carrierDetails: null,
				}));

			const onNextMiddleStep = async () => {
				const res = await this.updateCarrierDetails();
				if (res) {
					this.setState((prevState: models.IAccountAppCardState) => ({
						activeStep: prevState.activeStep + 1,
					}));
				}
			};

			dialogButtons.push(
				scacSelectionButton,
				middleStepsButtons(onBackMiddleStep, onNextMiddleStep, !this.state.selectedCarrier),
				middleStepsButtons(onBack2ndMiddleStep, onNext2ndMiddleStep, false),
				finalStepButtons(false, onBackFinalStep, onSaveFinalStep)
			);
		}

		return dialogButtons;
	}

	private updateSelectedCarrier(carrier: models.ICarrier) {
		this.setState({
			selectedCarrier: carrier,
		});
	}

	private shouldDisplayTierOverride = (carrierCode: CarriersEnum) => {
		// Tier override works only for ePostage, eVS
		// UI is only visible to admin
		return (
			accountService.IsAdmin(this.userData.user.role) &&
			(carrierCode === CarriersEnum.USPSePostage ||
				carrierCode === CarriersEnum.USPSeVS ||
				carrierCode === CarriersEnum.USPSShip ||
				carrierCode === CarriersEnum.USPSeVSNative ||
				carrierCode === CarriersEnum.USPSePostageNative)
		);
	};

	private updateSteps(carrierCode: CarriersEnum, totalSteps: string[]) {
		switch (carrierCode) {
			case CarriersEnum.USPSeVS:
			case CarriersEnum.USPSeVSNative:
			case CarriersEnum.USPSePostage:
			case CarriersEnum.USPSePostageNative:
			case CarriersEnum.USPSShip:
				{
					const steps = [
						...totalSteps,
						this.props.intl.formatMessage({
							id: 'app.carrier-configuration-label',
						}),
					];

					if (this.shouldDisplayTierOverride(carrierCode)) {
						steps.push(
							this.props.intl.formatMessage({
								id: 'app.carrier-tier-override-label',
							})
						);
					}

					this.setState({
						steps,
					});
				}
				break;
			case CarriersEnum.USPSePostageWarehouse:
			case CarriersEnum.USPSePostagePostPay:
			case CarriersEnum.UPSNative:
			case CarriersEnum.FedExNative:
				this.setState({
					steps: [
						...totalSteps,
						this.props.intl.formatMessage({
							id: 'app.carrier-configuration-label',
						}),
					],
				});
				break;
			case CarriersEnum.UPSPHv2:
			case CarriersEnum.USPSPHv2:
			case CarriersEnum.USPSeVSWarehouse:
			case CarriersEnum.DHLExpressPHv2:
			default:
				this.setState({ steps: [...totalSteps] });
				break;
		}
	}

	private getCarrierConfigurationComponent() {
		const { code: carrierCode, name, logo, paymentType } = this.state.selectedCarrier;

		switch (carrierCode) {
			case CarriersEnum.USPSeVS:
			case CarriersEnum.USPSeVSNative:
			case CarriersEnum.USPSePostage:
			case CarriersEnum.USPSePostageNative:
			case CarriersEnum.USPSShip:
			case CarriersEnum.USPSePostageWarehouse:
			case CarriersEnum.USPSePostagePostPay:
				return (
					<USPSConfigComponent
						ref={this.carrierConfigRef}
						selectedCarrierName={name}
						selectedCarrierLogo={logo}
						preloadedData={{ ...this.state.carrierConfig }}
						{...{ carrierCode, paymentType }}
					/>
				);
			case CarriersEnum.UPSNative:
				return <UPSNativeComponent ref={this.carrierConfigRef} selectedCarrierName={name} selectedCarrierLogo={logo} />;
				break;
			case CarriersEnum.FedExNative:
				return (
					<FedexNativeComponent ref={this.carrierConfigRef} selectedCarrierName={name} selectedCarrierLogo={logo} />
				);
			case CarriersEnum.USPSeVS:
			case CarriersEnum.USPSPHv2:
			case CarriersEnum.UPSPHv2:
			case CarriersEnum.DHLExpressPHv2:
			case CarriersEnum.USPSeVSNative:
			case CarriersEnum.USPSeVSWarehouse:
			default:
				return null;
		}
	}

	private async updateCarrierDetails(): Promise<boolean> {
		try {
			const carrierDetails = await this.carrierDetailRef.current.getWrappedInstance().getFormValues();
			if (!!carrierDetails) {
				carrierData = { ...carrierData, ...carrierDetails };
				this.setState({ carrierDetails: { ...carrierDetails } });
				return true;
			}
			return false;
		} catch (err) {
			throw err;
		}
	}

	private async updateCarrierConfig(): Promise<boolean> {
		try {
			const configDetails = await this.carrierConfigRef.current.getWrappedInstance().getFormValues();
			if (!!configDetails) {
				this.setState({ carrierConfig: { ...configDetails } });
				return true;
			}
			return false;
		} catch (err) {
			throw err;
		}
	}

	private async saveCarrierDetails() {
		try {
			const res = await this.updateCarrierDetails();
			if (res) {
				this.addNewApp(carrierData, null, null);
			}
		} catch (err) {
			throw err;
		}
	}

	private deleteApp = async (appId: string) => {
		try {
			this.setState({
				dialogButtonLoading: true,
			});

			await accountService.DeleteApp(appId);
			this.setState(
				{
					dialogButtonLoading: false,
					openDeleteDialog: false,
				},
				() => {
					// Set passCustomText to false during dialog closure
					// in order to stop passing custom dialog button text
					CustomDialogButtonTextConfigs.passCustomText = false;
				}
			);
			// Success snackbar will be called inside parent function
			this.props.onAppDeletion(appId);
		} catch (error) {
			// Configure dialog description data for delete dialog
			this.setState({
				dialogButtonLoading: false,
				isDeleteAppFailed: true,
			});
			if (!Axios.isCancel(error)) {
				const DeletionFailureErrorCode = 'Account.App.Validation_Failed';
				const errors = sharedService.parseError(error.data);
				const errorCodes = sharedService.parseErrorCode(error.data);

				if (errorCodes.includes(DeletionFailureErrorCode.toUpperCase())) {
					const dialogDescriptionNode = (
						<>
							<Typography variant='body1'>{errors[0]}.</Typography>
							<Typography variant='body1'>
								{intlService.GetFormattedMessage('app.generic.deletion-failure-dialog-description')}
							</Typography>
						</>
					);

					// Set passCustomText to true
					// so that custom dialog button text get passed to the customDialog
					CustomDialogButtonTextConfigs.passCustomText = true;

					this.setState({
						dialogDescription: dialogDescriptionNode,
					});
					return;
				}
				this.props.openSnackBar(errors, SnackBarVariants.Error);
			}
		}
	};

	private updateAppStatus = async (disableApp?: boolean) => {
		try {
			this.setState({
				dialogButtonLoading: true,
			});
			const appData: models.IAccountAppResponseVM = {
				...this.state.app,
				status: this.isAppEnabled || disableApp ? EnableDisableEnum.Disabled : EnableDisableEnum.Enabled,
			};

			await accountService.editApp(appData);

			// this call has been made in order to close
			// delete dialog whenever deletion operation fails and
			// user wishes to disable it instead
			this.setState({
				openDeleteDialog: false,
				dialogButtonLoading: false,
			});
			// Snackbar for success message will be called inside parent component
			this.props.onAppStatusUpdate(appData.appId, appData.status);
		} catch (error) {
			this.setState({
				openDeleteDialog: false,
				openAppStatusDialog: false,
				dialogButtonLoading: false,
			});
			throw error;
		}
	};

	private closeDeleteDialog = async (dialogPanel: string, isCancel: boolean) => {
		try {
			if (isCancel) {
				this.setState(
					{
						openDeleteDialog: false,
						isDeleteAppFailed: false,
					},
					() => {
						// Set passCustomText to false during dialog closure
						// in order to stop passing custom dialog button text
						CustomDialogButtonTextConfigs.passCustomText = false;
					}
				);
				return;
			}

			// If deleting an app operation fails
			// then call App status update API on confirmation
			if (this.state.isDeleteAppFailed) {
				await this.updateAppStatus(true);
				this.setState({
					isDeleteAppFailed: false,
				});
				return;
			}

			// else call delete operation
			await this.deleteApp(this.state.app.appId);
		} catch (error) {
			this.setState({
				dialogButtonLoading: false,
			});
			throw error;
		}
	};

	private closeAppStatusDialog = async (dialogPanel: string, isCancel: boolean) => {
		if (!isCancel) {
			await this.updateAppStatus();
		}
		this.setState({
			openAppStatusDialog: false,
		});
	};
}

export default withAxiosCancellation(withSnackBar(injectIntl(AccountAppCardComponent)));



// WEBPACK FOOTER //
// ./src/components/account/app/account-app-card.component.tsx